import { NervaUserData } from 'hydra-data-types';
import { isNonEmptyString } from 'ts-utils';
import { IbsClassification } from '../../../businessLogic/AssessmentQuestionTypes';

/** Answers that say nothing about the person, so they're never shown back to them. */
const NON_ANSWERS = new Set(['More/Other', 'Other', 'I’m not sure', "I'm not sure", 'None']);
const MAX_ROW_ITEMS = 3;

/** Diagnosis answers shortened to the names people know them by. */
const DIAGNOSIS_LABELS: Record<string, string> = {
  'Irritable Bowel Syndrome (IBS)': 'IBS',
  'Inflammatory Bowel Disease (IBD)': 'IBD',
  'Small Intestinal Bacterial Overgrowth (SIBO)': 'SIBO',
  'Gastro-oesophageal reflux disease (GERD)': 'GERD',
  'Gastrointestinal Reflux Disease (GERD)': 'GERD',
};

const SUBTYPE_LABELS: Record<string, string> = {
  [IbsClassification.IBS_C]: 'IBS-C (constipation)',
  [IbsClassification.IBS_D]: 'IBS-D (diarrhea)',
  [IbsClassification.IBS_M]: 'IBS-M (mixed)',
};

export type ProgramAnswers = {
  /** Diagnosed conditions, e.g. ["IBS", "GERD"] */
  conditions: string[];
  /** IBS or IBD subtype, e.g. "IBS-D (diarrhea)" */
  subtypes: string[];
};

/** Answers the shared Nerva type doesn't list yet, from the referred flow. */
export type ProgramUserData = Partial<NervaUserData> & {
  gutBrainDiagnosis?: string[];
  structuralConditions?: string[];
  ibdSubtype?: string[];
};

/** The assessment answers the payment page shows back to the person. */
export function programAnswers(userData: ProgramUserData): ProgramAnswers {
  const answered = (values: string[] | undefined) =>
    (values ?? []).filter((value) => !NON_ANSWERS.has(value));

  const diagnosed = [
    ...answered(userData.gutBrainDiagnosis),
    ...answered(userData.structuralConditions),
    ...answered(userData.otherGIIssues),
  ].map((diagnosis) => DIAGNOSIS_LABELS[diagnosis] ?? diagnosis);
  // A subtype answer stays saved when the person goes back and unticks its condition, so it only
  // counts while that condition is still ticked. The main flow asks the IBS type without an IBS
  // diagnosis question, so there the type stands in for one.
  const asksIbsDiagnosis = userData.gutBrainDiagnosis !== undefined;
  const ibsSubtype =
    !asksIbsDiagnosis || diagnosed.includes('IBS')
      ? SUBTYPE_LABELS[userData.classify?.[0] ?? '']
      : undefined;
  const ibdSubtype = diagnosed.includes('IBD') ? answered(userData.ibdSubtype)[0] : undefined;
  const diagnoses = [...(isNonEmptyString(ibsSubtype) ? ['IBS'] : []), ...diagnosed];

  return {
    conditions: [...new Set(diagnoses)],
    subtypes: [ibsSubtype, ibdSubtype].filter(isNonEmptyString),
  };
}

/** A card row's list, e.g. "IBS, GERD, SIBO"; past three the rest become "and more". */
export function listForRow(values: string[]): string {
  const shown = values.slice(0, MAX_ROW_ITEMS).join(', ');
  return values.length > MAX_ROW_ITEMS ? `${shown} and more` : shown;
}
